import { createStart, createCsrfMiddleware, createMiddleware } from "@tanstack/react-start";

import { renderErrorPage } from "./lib/error-page";
import { attachSupabaseAuth } from "@/integrations/supabase/auth-attacher";
import { CORRELATION_HEADER, correlationId } from "./lib/flow";

/**
 * Sends the active browser correlation id with every server-function call so a
 * server-side Sentry event can be joined to the user action that caused it.
 */
const correlationMiddleware = createMiddleware({ type: "function" }).client(async ({ next }) =>
  next({ headers: { [CORRELATION_HEADER]: correlationId() } }),
);

/**
 * Small clock differences between the browser and the token verifier make
 * Supabase reject a freshly minted access token with "JWT issued at future".
 * The call never reaches the handler in that case, so retrying once after a
 * forced session refresh is safe and invisible to the volunteer.
 */
const AUTH_RETRYABLE = /jwt issued at future|invalid token|invalid jwt|jwt expired/i;

const authRetryMiddleware = createMiddleware({ type: "function" }).client(async ({ next }) => {
  try {
    return await next();
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error ?? "");
    if (!AUTH_RETRYABLE.test(message)) throw error;
    const { supabase } = await import("@/integrations/supabase/client");
    await supabase.auth.refreshSession().catch(() => undefined);
    await new Promise((resolve) => setTimeout(resolve, 1_000));
    return await next();
  }
});




const errorMiddleware = createMiddleware().server(async ({ next, request }) => {
  const url = new URL(request.url);
  const isServerFn =
    request.headers.get("x-tsr-serverfn") === "true" || url.pathname.startsWith("/_serverFn");
  if (url.pathname.startsWith("/lovable/")) {
    return next();
  }
  try {
    return await next();
  } catch (error) {
    if (error != null && typeof error === "object" && "statusCode" in error) {
      throw error;
    }
    if (error instanceof Response) {
      throw error;
    }
    const { isUserFacingError } = await import("./lib/user-error");
    // Expected validation messages belong to the person using the app, not to
    // the error inbox: surface them, do not report them.
    if (isUserFacingError(error)) {
      throw error;
    }
    console.error(error);
    const { captureServerException, userFromRequest } = await import("./lib/sentry.server");
    await captureServerException(error, {
      request,
      user: userFromRequest(request),
      correlationId: request.headers.get("x-correlation-id"),
      tags: { surface: isServerFn ? "server_function" : "ssr" },
    });

    if (isServerFn) {
      throw error;
    }
    return new Response(renderErrorPage(), {
      status: 500,
      headers: { "content-type": "text/html; charset=utf-8" },
    });
  }
});



// Start installs this automatically when src/start.ts is absent; defining the
// file opts out, so re-add it explicitly to keep server functions protected
// from cross-site requests.
const csrfMiddleware = createCsrfMiddleware({
  filter: (ctx) => ctx.handlerType === "serverFn",
});

export const startInstance = createStart(() => ({
  functionMiddleware: [authRetryMiddleware, attachSupabaseAuth, correlationMiddleware],
  requestMiddleware: [errorMiddleware, csrfMiddleware],
}));
